import { compareTwoDates, shiftDate, isNotNullOrUndefined } from '_shared_/utils/utils';

const getters = {
  getReturnsPageData: state => state.returnsPageData,
  getReturnsPageIndexIds: state => state.returnsPageIndexIds,
  getReturnsPageFundAbbreviations: state => state.returnsPageFundAbbreviations
};

const getReturnsValue = (isTooYoung, returnsData) => {
  if (isTooYoung) return 'N/A';
  if (!isNaN(returnsData) && isNotNullOrUndefined(returnsData)) return Number(returnsData);
  return '--';
};

const mutations = {
  SET_RETURNS_PAGE(state, payload) {
    state.returnsPageData = {
      audience: 'advisor',
      dates: payload.dates || {},
      objectives: payload.objectives || {},
      returns: {
        monthly: [],
        quarterly: []
      }
    };
    payload?.returns?.length && payload.returns.forEach(item => {
      const today = new Date();
      const useCumulative = compareTwoDates(item.FundInceptionDate, shiftDate(new Date(payload.dates['1'].MonthlyAsOfDate), -1, 'y').toLocaleDateString('en-US')) >= 0;
      const isInceptionLessThan1yr = compareTwoDates(item.FundInceptionDate, shiftDate(today, -1, 'y').toLocaleDateString('en-US')) > 0;
      const isInceptionLessThan13mo = compareTwoDates(item.FundInceptionDate, shiftDate(today, -13, 'm').toLocaleDateString('en-US')) > 0;
      const isInceptionLessThan3yr = compareTwoDates(item.FundInceptionDate, shiftDate(today, -3, 'y').toLocaleDateString('en-US')) > 0;
      const isInceptionLessThan5yr = compareTwoDates(item.FundInceptionDate, shiftDate(today, -5, 'y').toLocaleDateString('en-US')) > 0;
      const isInceptionLessThan10yr = compareTwoDates(item.FundInceptionDate, shiftDate(today, -10, 'y').toLocaleDateString('en-US')) > 0;
      const isInceptionLessThan15yr = compareTwoDates(item.FundInceptionDate, shiftDate(today, -15, 'y').toLocaleDateString('en-US')) > 0;

      state.returnsPageData.returns.monthly.push({
        shareClass: item.ShareClass,
        assetType: item.AssetType,
        dailyReturnsYTD: item.DailyReturnsYTD,
        expenseRatioGross: item.ExpenseRatioGross,
        expenseRatioNet: item.ExpenseRatioNet,
        fundGroupDescription: item.FundGroupDescription,
        fundInceptionDate: item.FundInceptionDate,
        fundName: item.FundName,
        fundNameLegal: item.FundNameLegal,
        fundNumber: item.FundNumber,
        fundAbbreviatedName: item.FundAbbreviatedName,
        objective: item.Objective,
        objectiveOrder: item.ObjectiveOrder,
        //CG-18919 - if the inception of the fund is not old enough to have data for the time period, return 'N/A'. If the inception is old enough return the value or '--' if there is no data
        returns1Yr: getReturnsValue(isInceptionLessThan1yr, item.MonthlyReturns1Y),
        returns3Yr: getReturnsValue(isInceptionLessThan3yr, item.MonthlyReturns3Y),
        returns5Yr: getReturnsValue(isInceptionLessThan5yr, item.MonthlyReturns5Y),
        returns10Yr: getReturnsValue(isInceptionLessThan10yr, item.MonthlyReturns10Y),
        returns15Yr: getReturnsValue(isInceptionLessThan15yr, item.MonthlyReturns15Y),
        //CG-18919 & CG-18993 - If 1yr or less, show cumulative. If older than 1yr, show annual. If there is no data, use double dash
        returnsLTD: useCumulative
          ? (!!Number(item.MonthlyReturnsCumlLTD) && item.MonthlyReturnsCumlLTD || '--')
          : (!!Number(item.MonthlyReturnsAnnualLTD) && item.MonthlyReturnsAnnualLTD || '--'),
        returnsYTD: item.MonthlyReturnsYTD,
        indexName: item.IndexName,
        indexId: item.IndexId,
        //CG-23156: these two are only used when passing Quarter-End data to the Returns getTableData, but fails tests without them here too
        indexQtrName: item?.IndexQtrName,
        indexQtrId: item?.IndexQtrId,
        indexReturns1Yr: getReturnsValue(isInceptionLessThan1yr, item.IndexMonthlyReturns1Y),
        indexReturns3Yr: getReturnsValue(isInceptionLessThan3yr, item.IndexMonthlyReturns3Y),
        indexReturns5Yr: getReturnsValue(isInceptionLessThan5yr, item.IndexMonthlyReturns5Y),
        indexReturns10Yr: getReturnsValue(isInceptionLessThan10yr, item.IndexMonthlyReturns10Y),
        indexReturns15Yr: getReturnsValue(isInceptionLessThan15yr, item.IndexMonthlyReturns15Y),
        /*CG-21075 - Index Returns as of fund lifetime
				 * if fund is less than 13 months old, show CLTD;
				 * if older, show LTD;
				 * if there is no numerical data, use N/A
				 * (FWIW: the API's returnsAllRepository mapResponseToModel replaces -32768 with "N/A")
				*/
        indexReturnsLifetime: isInceptionLessThan13mo
          ? (!!Number(item.IndexMonthlyReturnsCLTD) && item.IndexMonthlyReturnsCLTD || 'N/A')
          : (!!Number(item.IndexMonthlyReturnsLTD) && item.IndexMonthlyReturnsLTD || 'N/A'),
        indexReturnsYTD: item.IndexMonthlyReturnsYTD,
        trademarkLegal: item.TrademarkLegal
      });
      state.returnsPageData.returns.quarterly.push({
        shareClass: item.ShareClass,
        assetType: item.AssetType,
        dailyReturnsYTD: item.DailyReturnsYTD,
        expenseRatioGross: item.ExpenseRatioGross,
        expenseRatioNet: item.ExpenseRatioNet,
        fundGroupDescription: item.FundGroupDescription,
        fundInceptionDate: item.FundInceptionDate,
        fundName: item.FundName,
        fundNameLegal: item.FundNameLegal,
        fundNumber: item.FundNumber,
        fundAbbreviatedName: item.FundAbbreviatedName,
        objective: item.Objective,
        objectiveOrder: item.ObjectiveOrder,
        //if the inception of the fund is not old enough to have data for the time period, return 'N/A'. If the inception is old enough return the value or '--' if there is no data
        returns1Yr: getReturnsValue(isInceptionLessThan1yr, item.QtrReturns1Y),
        returns3Yr: getReturnsValue(isInceptionLessThan3yr, item.QtrReturns3Y),
        returns5Yr: getReturnsValue(isInceptionLessThan5yr, item.QtrReturns5Y),
        returns10Yr: getReturnsValue(isInceptionLessThan10yr, item.QtrReturns10Y),
        returns15Yr: getReturnsValue(isInceptionLessThan15yr, item.QtrReturns15Y),
        //if 1yr or less, show cumulative. If older than 1yr, show annual. If there is no data, use double dash
        returnsLTD: useCumulative
          ? (!!Number(item.MonthlyReturnsCumlLTD) && item.MonthlyReturnsCumlLTD || '--')
          : (!!Number(item.MonthlyReturnsAnnualLTD) && item.MonthlyReturnsAnnualLTD || '--'),
        returnsYTD: item.MonthlyReturnsYTD,
        indexName: item.IndexName,
        indexId: item.IndexId,
        indexQtrName: item?.IndexQtrName,
        indexQtrId: item?.IndexQtrId,
        indexReturns1Yr: getReturnsValue(isInceptionLessThan1yr, item.IndexQtrReturns1Y),
        indexReturns3Yr: getReturnsValue(isInceptionLessThan3yr, item.IndexQtrReturns3Y),
        indexReturns5Yr: getReturnsValue(isInceptionLessThan5yr, item.IndexQtrReturns5Y),
        indexReturns10Yr: getReturnsValue(isInceptionLessThan10yr, item.IndexQtrReturns10Y),
        indexReturns15Yr: getReturnsValue(isInceptionLessThan15yr, item.IndexQtrReturns15Y),
        //see CG-21075 note above
        indexReturnsLifetime: isInceptionLessThan13mo
          ? (!!Number(item.IndexQtrReturnsCLTD) && item.IndexQtrReturnsCLTD || 'N/A')
          : (!!Number(item.IndexQtrReturnsLTD) && item.IndexQtrReturnsLTD || 'N/A'),
        indexReturnsYTD: item?.IndexQtrMonthlyReturnsYTD, //CG-23173
        trademarkLegal: item.TrademarkLegal
      });
    });
  },
  SET_RETURNS_PAGE_INDEX_IDS(state, payload) {
    state.returnsPageIndexIds = payload;
  },
  SET_RETURNS_PAGE_FUND_ABBREVIATIONS(state, payload) {
    state.returnsPageFundAbbreviations = payload;
  }
};

const state = {
  returnsPageData: null,
  returnsPageIndexIds: null,
  returnsPageFundAbbreviations: null
};

export default {
  getReturnsValue,
  getters,
  mutations,
  state
};
